/* @HEADER
 * @version 1.0.1813 | 2026-09-21
 * @purpose Space navigation tree for the sidebar — shows default board + user spaces, inline create/delete.
 * @sidecar BoardNav.tsx.header.md
 * @layer app | @hex adapter | @ctx board
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6373, ZVX-DEV-6225, ZVX-DEV-6172, ZVX-DEV-6103, ZVX-E2E-002, ZVX-DEV-2023

import { useState, useRef, useEffect } from 'react'
import type { BoardGroupRecord } from '../lib/db'
import { SpaceIcon, ProjectTasksIcon, OrgTasksIcon, AllProjectsIcon } from './NavIcons'
import { BOARD_NAV_TESTIDS } from './board-nav.testids'
import { ALL_TASKS_TESTIDS, ALL_PROJECTS_TESTIDS } from '../features/kanban/board.testids'
import { useMessages } from '../shared/i18n/useMessages'
import { SharedBadge } from '../features/content-access/SharedBadge'
import { useSharedContentIds } from '../features/content-access/useSharedContentIds'
import { spaceAccessContentId } from '../features/content-access/spaceAccessContentId'
// The SAME source App.tsx reads when it builds the wire id for the access
// panel (ZVX-DEV-4822). Two ways of deriving one id is how the badge and the
// panel would end up disagreeing about which space they are about.
import { getActiveProject } from '../lib/projectStore'
import { isAllTasksSpace, isAllProjectsSpace, isOrgTasksSpace, isUndeletableSpace } from '@modules/board/public-api.mjs'

interface BoardNavProps {
  groups: BoardGroupRecord[]
  collapsed: boolean
  activeGroupId: string | null
  onSelectGroup: (groupId: string) => void
  onCreateGroup: (name: string, parentId: string | null) => void
  onDeleteGroup: (groupId: string) => void
  /**
   * ZVX-DEV-2468 (S11): whether a new space may be created under the effective
   * entitlement's `spaceLimit`. Defaults to `true` so any caller that does not
   * yet pass it (or when enforcement is disabled) keeps the old behavior.
   */
  canCreateSpace?: boolean
  /** ZVX-DEV-2468 (S11): opens the shared upsell placard when creation is blocked. */
  onSpaceLimitBlocked?: () => void
  /**
   * ZVX-DEV-3616: does the viewer belong to an organisation? Gates «Все задачи
   * организации» — a person with no organisation does not get the entry at all,
   * absent rather than empty, because an empty board reads as "we have nothing"
   * instead of "this is not for you". Defaults to `false` so a caller that has
   * not been taught about it shows the nav it showed before.
   */
  hasOrganisation?: boolean
  /**
   * ZVX-DEV-6225 (C6): is the organisation on screen the PERSONAL one? Then the
   * summary is called «Все личные задачи» — the operator did not want the word
   * «организации» in «Личное». Defaults to `false` so a caller that has not
   * been taught about it shows the label it showed before.
   */
  personalOrganisation?: boolean
  /**
   * ZVX-DEV-3618: the `cross_project` entitlement gate for «Все проекты». It
   * used to sit on the duplicate top-level nav entry that this slice removed;
   * it moves here with the entry it guards. Defaults to `true` so a caller that
   * does not pass it keeps showing what it showed before.
   */
  crossProjectAllowed?: boolean
  /** Neutral modal intent; BoardNav knows neither sections nor persistence. */
  onOpenSpaceSettings?: (intent: { spaceId: string; sectionId?: string; focusEntityId?: string }) => void
}

export function BoardNav({
  groups,
  collapsed,
  activeGroupId,
  onSelectGroup,
  onCreateGroup,
  onDeleteGroup,
  canCreateSpace = true,
  onSpaceLimitBlocked,
  hasOrganisation = false,
  personalOrganisation = false,
  crossProjectAllowed = true,
  onOpenSpaceSettings,
}: BoardNavProps) {
  const m = useMessages()
  const [creating, setCreating] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const submittedRef = useRef(false)

  useEffect(() => { if (creating !== null) inputRef.current?.focus() }, [creating])

  // ZVX-DEV-4837 -- which of these spaces reach beyond the organisation-wide
  // default. Asked for the whole list at once: per-row requests would make the
  // badges appear one by one, which reads as items becoming shared while you
  // watch. An unanswered list stays UNBADGED rather than being shown as private.
  const projectId = getActiveProject()?.serverProjectId ?? null
  const spaceWireIds = groups.map((g) => spaceAccessContentId(g.id, projectId))
  const sharedSpaceIds = useSharedContentIds('space', spaceWireIds)

  if (collapsed) return null

  // ZVX-DEV-2023: the «Все задачи» system space is pinned FIRST as a system nav
  // item (no delete/+ controls); the 'default' catch-all is reached via the
  // parent "Задачи"→«Входящие» nav item, so it stays out of the user-space list.
  const allTasksSpace = groups.find(g => g.parent_id === null && isAllTasksSpace(g))
  // ZVX-DEV-3615: «Все проекты» is pinned as a system item too. It used to fall
  // through into the user-space list below, which is why it was drawn with a
  // delete control it must never have.
  const allProjectsSpace = groups.find(g => g.parent_id === null && isAllProjectsSpace(g))
  // ZVX-DEV-3616: «Все задачи организации» sits between the two — wider than
  // this project, narrower than every project.
  const orgTasksSpace = hasOrganisation
    ? groups.find(g => g.parent_id === null && isOrgTasksSpace(g))
    : undefined
  // ZVX-DEV-3615: exclude every UNDELETABLE space from the user-space list, not
  // just the two this filter happened to name. It listed 'default' by literal id
  // and all-tasks by predicate, and forgot «Все проекты» entirely — so that
  // aggregate was rendered with a ✕ that the rule then refused, silently. The
  // list and the rule now consult the same predicate, which is the only way
  // they cannot drift apart again.
  const topLevel = groups.filter(
    g => g.parent_id === null && !isUndeletableSpace(g),
  )
  const childrenOf = (parentId: string) => groups.filter(g => g.parent_id === parentId)

  function handleSubmit(parentId: string | null) {
    if (submittedRef.current) return
    submittedRef.current = true
    if (newName.trim()) onCreateGroup(newName.trim(), parentId)
    setCreating(null)
    setNewName('')
    setTimeout(() => { submittedRef.current = false }, 100)
  }

  return (
    <ul className="board-nav" role="tree">
      {/* ZVX-DEV-3618 laid the three aggregates narrow -> wide; ZVX-DEV-6172
          (B7) turns them round: every project, the organisation, this project —
          wide -> narrow. Same order as `sortSpacesForOverview`, which is the
          canonical one; none of them carries a delete or + control.
          «Все проекты» keeps the `cross_project` entitlement gate that used to
          live on the removed top-level duplicate. */}
      {allProjectsSpace && crossProjectAllowed && (
        <li role="treeitem">
          <div className="board-nav__row">
            <button
              className={`board-nav__item board-nav__item--all-projects${activeGroupId === allProjectsSpace.id ? ' board-nav__item--active' : ''}`}
              onClick={() => onSelectGroup(allProjectsSpace.id)}
              data-testid={ALL_PROJECTS_TESTIDS.navItem}
              title={m.space.allProjects.systemCue}
            >
              <span className="board-nav__icon"><AllProjectsIcon /></span>
              <span className="board-nav__label">{m.space.allProjects.label}</span>
            </button>
          </div>
        </li>
      )}
      {orgTasksSpace && (
        <li role="treeitem">
          <div className="board-nav__row">
            <button
              className={`board-nav__item board-nav__item--org-tasks${activeGroupId === orgTasksSpace.id ? ' board-nav__item--active' : ''}`}
              onClick={() => onSelectGroup(orgTasksSpace.id)}
              data-testid={BOARD_NAV_TESTIDS.orgTasksItem}
              title={personalOrganisation ? m.space.orgTasks.personalSystemCue : m.space.orgTasks.systemCue}
            >
              <span className="board-nav__icon"><OrgTasksIcon /></span>
              <span className="board-nav__label">{personalOrganisation ? m.space.orgTasks.personalLabel : m.space.orgTasks.label}</span>
            </button>
          </div>
        </li>
      )}
      {allTasksSpace && (
        <li role="treeitem">
          <div className="board-nav__row">
            <button
              className={`board-nav__item board-nav__item--all-tasks${activeGroupId === allTasksSpace.id ? ' board-nav__item--active' : ''}`}
              onClick={() => onSelectGroup(allTasksSpace.id)}
              data-testid={ALL_TASKS_TESTIDS.navItem}
              title={m.space.allTasks.systemCue}
            >
              <span className="board-nav__icon"><ProjectTasksIcon /></span>
              <span className="board-nav__label">{m.space.allTasks.label}</span>
            </button>
          </div>
        </li>
      )}
      {/* User-created spaces (default space is accessed via "Задачи" nav item directly) */}
      {topLevel.map(group => (
        <li key={group.id} role="treeitem">
          <div className="board-nav__row">
            <button
              className={`board-nav__item${activeGroupId === group.id ? ' board-nav__item--active' : ''}`}
              onClick={() => onSelectGroup(group.id)}
              data-testid={BOARD_NAV_TESTIDS.spaceItem(group.id)}
            >
              <span className="board-nav__icon"><SpaceIcon /></span>
              <span className="board-nav__label">{group.name}</span>
            </button>
            {sharedSpaceIds.has(spaceAccessContentId(group.id, projectId)) && (
              <SharedBadge contentId={group.id} />
            )}
            {onOpenSpaceSettings && (
              <button
                type="button"
                className="board-nav__action board-nav__action--settings"
                onClick={e => { e.stopPropagation(); onOpenSpaceSettings({ spaceId: group.id, sectionId: 'directions' }) }}
                title={m.spaceSettings.openLabel(group.name)}
                aria-label={m.spaceSettings.openLabel(group.name)}
                data-testid={BOARD_NAV_TESTIDS.spaceSettingsBtn(group.id)}
              >⋯</button>
            )}
            <button
              className="board-nav__action"
              onClick={e => { e.stopPropagation(); if (!canCreateSpace) { onSpaceLimitBlocked?.(); return } setCreating(group.id); setNewName('') }}
              title={m.nav.addSubspace}
              data-testid={BOARD_NAV_TESTIDS.newSubSpaceBtn(group.id)}
            >+</button>
            <button
              className="board-nav__action board-nav__action--delete"
              onClick={e => { e.stopPropagation(); onDeleteGroup(group.id) }}
              title={m.nav.deleteSpace}
              data-testid={BOARD_NAV_TESTIDS.deleteSpaceBtn(group.id)}
            >✕</button>
          </div>

          {(childrenOf(group.id).length > 0 || creating === group.id) && (
            <ul className="board-nav__children" role="group">
              {childrenOf(group.id).map(child => (
                <li key={child.id}>
                  <div className="board-nav__row">
                    <button
                      className={`board-nav__item board-nav__item--child${activeGroupId === child.id ? ' board-nav__item--active' : ''}`}
                      onClick={() => onSelectGroup(child.id)}
                    >
                      <span className="board-nav__label">{child.name}</span>
                    </button>
                    {onOpenSpaceSettings && (
                      <button
                        type="button"
                        className="board-nav__action board-nav__action--settings"
                        onClick={e => { e.stopPropagation(); onOpenSpaceSettings({ spaceId: child.id, sectionId: 'directions' }) }}
                        title={m.spaceSettings.openLabel(child.name)}
                        aria-label={m.spaceSettings.openLabel(child.name)}
                        data-testid={BOARD_NAV_TESTIDS.spaceSettingsBtn(child.id)}
                      >⋯</button>
                    )}
                    <button
                      className="board-nav__action board-nav__action--delete"
                      onClick={e => { e.stopPropagation(); onDeleteGroup(child.id) }}
                      title={m.nav.deleteSpace}
                    >✕</button>
                  </div>
                </li>
              ))}
              {creating === group.id && (
                <li className="board-nav__create-input">
                  <input
                    ref={inputRef}
                    className="board-nav__input"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSubmit(group.id)
                      if (e.key === 'Escape') { setCreating(null); setNewName('') }
                    }}
                    onBlur={() => handleSubmit(group.id)}
                    placeholder={m.nav.newSpaceNamePlaceholder}
                    data-testid={BOARD_NAV_TESTIDS.newSpaceInput}
                  />
                </li>
              )}
            </ul>
          )}
        </li>
      ))}

      {creating === '__top__' && (
        <li className="board-nav__create-input">
          <input
            ref={inputRef}
            className="board-nav__input"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit(null)
              if (e.key === 'Escape') { setCreating(null); setNewName('') }
            }}
            onBlur={() => handleSubmit(null)}
            placeholder={m.nav.newTopSpaceNamePlaceholder}
            data-testid={BOARD_NAV_TESTIDS.newSpaceInput}
          />
        </li>
      )}

      {/* ZVX-DEV-5351: «Поделились со мной» used to render HERE, inside the
          space tree. It holds canvases and wiki pages as well as spaces, so a
          place under «Задачи» said the wrong thing about it. It now sits beside
          the tools in `AppShell`, after «Аналитика», where the operator asked
          for it. */}

      <li>
        <button
          className="board-nav__add-btn"
          onClick={() => { if (!canCreateSpace) { onSpaceLimitBlocked?.(); return } setCreating('__top__'); setNewName('') }}
          data-testid={BOARD_NAV_TESTIDS.newSpaceBtn}
        >
          + Пространство
        </button>
      </li>
    </ul>
  )
}
